import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import WorkflowEditorWithProvider from "../../workflow/WorkflowEditor";
import {
  selectActiveCompany,
  selectNodeConfigs,
} from "../../../../core/data/redux/automations/selector";
import { fetchAllNodeConfigs } from "../../../../core/data/redux/automations/thunks";
import { resetWorkspaceState } from "../../../../core/data/redux/automations/slice";
import { notify } from "../../../../services/toast.config";
import Loader from "../../../../core/common/loader";

const AutomationCreate: React.FC = () => {
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const activeCompany = useSelector(selectActiveCompany);
  const nodeConfigs = useSelector(selectNodeConfigs);

  const companyId = activeCompany?.company_id;
  const companyType = activeCompany?.company_type;

  // Persist workflowCategory from location.state in component state
  const [workflowCategory, setWorkflowCategory] = useState<string | null>(
    location?.state?.workflowCategory || null
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Capture workflowCategory from location.state once and persist it
  useEffect(() => {
    if (location?.state?.workflowCategory && !workflowCategory) {
      setWorkflowCategory(location.state.workflowCategory);
    }
  }, [location?.state?.workflowCategory, workflowCategory]);

  useEffect(() => {
    dispatch(resetWorkspaceState());
    return () => dispatch(resetWorkspaceState());
  }, [dispatch]);

  useEffect(() => {
    if (!companyId) {
      setError("Please select a company first");
      return;
    }

    if (!workflowCategory) {
      setError("Workflow category is required");
      return;
    }

    const loadConfigs = async () => {
      setLoading(true);
      setError(null);
      try {
        await dispatch(
          fetchAllNodeConfigs({
            companyId: companyId,
            companyType: companyType,
            workflowType: workflowCategory,
          })
        ).unwrap();
      } catch (err: any) {
        const msg = err?.message || "Failed to load node configurations";
        setError(msg);
        notify.error(msg);
      } finally {
        setLoading(false);
      }
    };

    loadConfigs();
  }, [dispatch, companyId, companyType, workflowCategory]);

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="alert alert-danger" role="alert">
            <h4 className="alert-heading">Error Loading Configurations</h4>
            <p>{error}</p>
            <hr />
            <p className="mb-0">
              Please try refreshing the page or contact support if the issue
              persists.
            </p>
          </div>
        </div>
        <footer className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </footer>
      </div>
    );
  }

  return (
    <>
      {loading && <Loader />}
      <div className="page-wrapper">
        <div className="content p-0">
          {!loading && nodeConfigs ? (
            <WorkflowEditorWithProvider
              nodeConfigs={nodeConfigs}
              workflowType={workflowCategory}
            />
          ) : (
            !loading && (
              <div className="empty-state">
                <i className="ti ti-alert-circle"></i>
                <h3>No Node Configurations Available</h3>
                <p>
                  Please contact your administrator to set up workflow nodes.
                </p>
              </div>
            )
          )}
        </div>

        <footer className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2025 © CMS.</p>
          <p>
            Designed & Developed By{" "}
            <Link to="#" className="text-primary">
              LGS
            </Link>
          </p>
        </footer>
      </div>
    </>
  );
};

export default AutomationCreate;
